import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    mergeFlyoutOptions,
    PickerFlyoutRowContext,
    usePickerChain,
    usePickerLevelOrThrow,
    type PickerFlyoutRowContextValue,
} from "./PickerChainContext";
import { PickerFlyoutLevel } from "./PickerFlyoutLevel";
import type {
    PickerFlyoutProps,
    PickerItemData,
    PickerItemValue,
    PickerSectionData,
} from "./Picker.types";

/** Hover open / close delay of a flyout row, in ms (spec "Flyouts"). */
export const FLYOUT_HOVER_DELAY_MS = 100;

const useDelayedAction = () => {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const cancel = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);
    const schedule = useCallback(
        (action: () => void) => {
            cancel();
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                action();
            }, FLYOUT_HOVER_DELAY_MS);
        },
        [cancel],
    );
    useEffect(() => cancel, [cancel]);
    // One object per mount: the row context memo (and so every memoised row
    // under it) depends on this identity.
    return useMemo(() => ({ schedule, cancel }), [schedule, cancel]);
};

/**
 * A row that opens a cascading level. `row` is the `Picker.Item` rendered in
 * the current list; the level's rows come from `items` / `sections` (nested
 * data keeps the same presentation) or from composed `children`. Opens on
 * hover after 100ms, on click, Enter or ArrowRight; closes on ArrowLeft /
 * Escape (focus back on the row), when the pointer leaves both the row and
 * the level, or when a sibling flyout opens.
 */
export const PickerFlyout = <T extends PickerItemValue>({
    row,
    items,
    sections,
    nested = "flyout",
    isAutoOpenTarget = false,
    children,
    ...ownOptions
}: PickerFlyoutProps<T>) => {
    const level = usePickerLevelOrThrow();
    const chain = usePickerChain();
    const options = mergeFlyoutOptions(chain?.flyoutDefaults, ownOptions);
    const id = useId();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const isOpen = level.openFlyoutId === id;
    const { setOpenFlyoutId, flyouts } = level;
    const hover = useDelayedAction();
    const isPointerInsideRef = useRef(false);

    // Auto-opened levels (top query) never take focus. The effect acts only
    // when the auto-open target moves onto or off this row, so keystrokes
    // that keep the target never re-assert it over a sibling the user
    // hovered or clicked, and only a level still auto-opened closes when the
    // target leaves; a hover or click turns the level into a user open.
    const [isAutoOpened, setIsAutoOpened] = useState(false);
    const isAutoOpenedRef = useRef(false);
    const open = useCallback(() => {
        hover.cancel();
        isAutoOpenedRef.current = false;
        setIsAutoOpened(false);
        setOpenFlyoutId(id);
    }, [hover, id, setOpenFlyoutId]);
    useEffect(() => {
        if (isAutoOpenTarget) {
            isAutoOpenedRef.current = true;
            setIsAutoOpened(true);
            setOpenFlyoutId(id);
            return;
        }
        if (isAutoOpenedRef.current) {
            isAutoOpenedRef.current = false;
            setIsAutoOpened(false);
            setOpenFlyoutId((current) => (current === id ? null : current));
        }
    }, [isAutoOpenTarget, id, setOpenFlyoutId]);
    /** Keyboard close: the level goes away and the row takes focus back. */
    const closeToRow = useCallback(() => {
        hover.cancel();
        setOpenFlyoutId(null);
        anchor?.focus();
    }, [anchor, hover, setOpenFlyoutId]);

    useEffect(() => {
        const registry = flyouts.current;
        registry.set(id, { open });
        return () => {
            registry.delete(id);
        };
    }, [flyouts, id, open]);

    const scheduleClose = useCallback(() => {
        hover.schedule(() => {
            if (!isPointerInsideRef.current) {
                setOpenFlyoutId((current) => (current === id ? null : current));
            }
        });
    }, [hover, id, setOpenFlyoutId]);

    const rowContext = useMemo<PickerFlyoutRowContextValue>(
        () => ({
            id,
            isOpen,
            open,
            setAnchor,
            onPointerEnter: () => {
                isPointerInsideRef.current = true;
                if (!isOpen) {
                    hover.schedule(open);
                }
            },
            onPointerLeave: () => {
                isPointerInsideRef.current = false;
                if (isOpen) {
                    scheduleClose();
                } else {
                    hover.cancel();
                }
            },
        }),
        [id, isOpen, open, hover, scheduleClose],
    );

    const levelItems:
        | ReadonlyArray<PickerItemData<PickerItemValue>>
        | undefined = items;
    const levelSections:
        | ReadonlyArray<PickerSectionData<PickerItemValue>>
        | undefined = sections;

    return (
        <>
            <PickerFlyoutRowContext.Provider value={rowContext}>
                {row}
            </PickerFlyoutRowContext.Provider>
            {isOpen && anchor !== null && (
                <PickerFlyoutLevel
                    anchor={anchor}
                    options={options}
                    items={levelItems}
                    sections={levelSections}
                    nested={nested}
                    isAutoOpened={isAutoOpened}
                    onCloseToRow={closeToRow}
                    onPointerEnter={() => {
                        isPointerInsideRef.current = true;
                        hover.cancel();
                    }}
                    onPointerLeave={() => {
                        isPointerInsideRef.current = false;
                        scheduleClose();
                    }}
                >
                    {children}
                </PickerFlyoutLevel>
            )}
        </>
    );
};
